import * as Sentry from "@sentry/react";

import { logError } from "../../utils/error";

// Types for stream processing
export interface ProcessStreamParams {
  stream: ReadableStream<Uint8Array>;
  onMessageDelta: (delta: string) => void;
  onThinkingDelta?: (delta: string, type: string) => void;
}

interface StreamState {
  fullResponse: string;
  thinkingContent: string;
  usage: any;
}

interface ChunkProcessorParams {
  chunk: any;
  state: StreamState;
  onMessageDelta: (delta: string) => void;
  onThinkingDelta?: (delta: string, type: string) => void;
}

interface LineProcessorParams {
  line: string;
  state: StreamState;
  onMessageDelta: (delta: string) => void;
  onThinkingDelta?: (delta: string, type: string) => void;
}

// Helper to process a single chunk
const processChunk = (params: ChunkProcessorParams) => {
  const { chunk, state, onMessageDelta, onThinkingDelta } = params;

  if (chunk.type === "message_start") {
    const { message: { usage: messageUsage } } = chunk;
    state.usage = {
      ...state.usage,
      ...messageUsage
    };
    return;
  }

  if (chunk.type === "message_delta") {
    const { usage: chunkUsage } = chunk;
    state.usage = {
      ...state.usage,
      ...chunkUsage
    };
    return;
  }

  if (chunk.type !== "content_block_delta") {
    return;
  }

  // Handle regular text delta
  if (chunk.delta.type === "text_delta") {
    const delta = chunk.delta.text;
    state.fullResponse += delta;
    onMessageDelta(delta);
    return;
  }

  if (chunk.delta.type === "thinking_delta" && chunk.delta.thinking && onThinkingDelta) {
    state.thinkingContent += chunk.delta.thinking;
    onThinkingDelta(chunk.delta.thinking, "thinking");
    return;
  }
};

// Helper to process a single line
const processLine = (params: LineProcessorParams) => {
  const { line, state, onMessageDelta, onThinkingDelta } = params;
  if (!line.startsWith("data: ")) {
    return;
  }

  const data = line.slice(6);

  try {
    const chunk = JSON.parse(data);
    processChunk({ chunk, state, onMessageDelta, onThinkingDelta });
  } catch (e) {
    logError("Error parsing SSE chunk", e);
    Sentry.captureException(e, {
      tags: {
        function: "processLine",
        module: "claude-query"
      },
      extra: {
        line: line.substring(0, 100),
        data: data.substring(0, 100)
      }
    });
  }
};

// Process stream helper
export const processStream = async (
  params: ProcessStreamParams
): Promise<{responseText: string; thinking?: string; usage?: any}> => {
  const { stream, onMessageDelta, onThinkingDelta } = params;

  const state: StreamState = {
    fullResponse: "",
    thinkingContent: "",
    usage: {}
  };

  try {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    let result = await reader.read();

    while (!result.done) {
      buffer += decoder.decode(result.value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      lines.forEach(line => processLine({ line, state, onMessageDelta, onThinkingDelta }));

      result = await reader.read();
    }

    // Process any remaining buffer content that didn't end with newline
    if (buffer.trim()) {
      // Handle incomplete line at end of stream
      const finalLine = buffer.startsWith("data: ") ? buffer : `data: ${buffer}`;
      processLine({ line: finalLine, state, onMessageDelta, onThinkingDelta });
    }

    reader.releaseLock();

    return {
      responseText: state.fullResponse,
      thinking: state.thinkingContent || undefined,
      usage: state.usage || undefined
    };
  } catch (err) {
    logError("Stream error or interrupted", err);
    Sentry.captureException(err, {
      tags: {
        function: "processStream",
        module: "claude-query"
      },
      extra: {
        hasFullResponse: !!state.fullResponse,
        responseLength: state.fullResponse.length
      }
    });
    return {
      responseText: state.fullResponse,
      thinking: state.thinkingContent || undefined
    };
  }
};
